import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, Trash2, Plus, Eye, EyeOff, Globe, Building2, Image as ImageIcon } from 'lucide-react';
import { helpAPI } from '../api/client';
import { usePermissions } from '../context/PermissionsContext';
import Markdown from '../components/Markdown';

const empty = {
  CategoryID: '', Slug: '', Title: '', Summary: '', BodyMarkdown: '',
  AudienceRoles: '', SortOrder: 100, IsPublished: 1, Scope: 'company',
};

export default function HelpManage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { hasPermission } = usePermissions();
  const isNew = !id;
  const canGlobal = hasPermission('manage_global_help');

  const [list, setList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ ...empty, Scope: canGlobal ? 'global' : 'company' });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [editable, setEditable] = useState(true);
  const [uploading, setUploading] = useState(false);
  const bodyRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    helpAPI.getCategories().then(r => setCategories(r.data));
    helpAPI.adminListArticles().then(r => setList(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (isNew) {
      setForm({ ...empty, Scope: canGlobal ? 'global' : 'company' });
      setEditable(true);
      return;
    }
    setLoading(true);
    helpAPI.adminGetArticle(id)
      .then(r => {
        const a = r.data;
        setForm({
          CategoryID: a.CategoryID, Slug: a.Slug, Title: a.Title,
          Summary: a.Summary || '', BodyMarkdown: a.BodyMarkdown || '',
          AudienceRoles: a.AudienceRoles || '', SortOrder: a.SortOrder ?? 100,
          IsPublished: a.IsPublished ? 1 : 0,
          Scope: a.CompanyID ? 'company' : 'global',
        });
        setEditable(a.Editable !== false);
      })
      .finally(() => setLoading(false));
  }, [id, isNew, canGlobal]);

  const save = async () => {
    if (!form.CategoryID || !form.Slug || !form.Title) {
      alert('Category, Slug and Title are required'); return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const r = await helpAPI.adminCreateArticle(form);
        nav(`/help/manage/${r.data.articleId}`);
      } else {
        await helpAPI.adminUpdateArticle(id, form);
        alert('Saved');
      }
    } catch (e) {
      alert(e.response?.data?.error || e.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!confirm('Delete this article?')) return;
    try {
      await helpAPI.adminDeleteArticle(id);
      nav('/help/manage');
    } catch (e) {
      alert(e.response?.data?.error || 'Delete failed');
    }
  };

  const insertAtCursor = (text) => {
    const ta = bodyRef.current;
    if (!ta) {
      setForm(f => ({ ...f, BodyMarkdown: (f.BodyMarkdown || '') + '\n' + text }));
      return;
    }
    const start = ta.selectionStart ?? ta.value.length;
    const end = ta.selectionEnd ?? ta.value.length;
    const before = ta.value.slice(0, start);
    const after = ta.value.slice(end);
    const next = `${before}${text}${after}`;
    setForm(f => ({ ...f, BodyMarkdown: next }));
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + text.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { alert('Image must be 8 MB or smaller'); return; }
    setUploading(true);
    try {
      const r = await helpAPI.adminUploadImage(file, form.Scope || 'company');
      insertAtCursor(`\n${r.data.markdown}\n`);
    } catch (err) {
      alert(err.response?.data?.error || 'Image upload failed');
    } finally { setUploading(false); }
  };

  return (
    <div className="p-6">
      <Link to="/help" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to Help Center
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <aside className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">Articles</h3>
            <Link to="/help/manage" className="inline-flex items-center gap-1 text-xs bg-blue-600 text-white px-2 py-1 rounded">
              <Plus className="w-3 h-3" /> New
            </Link>
          </div>
          <div className="space-y-1 max-h-[70vh] overflow-y-auto">
            {list.map(a => {
              const isGlobal = !a.CompanyID;
              return (
                <Link key={a.ArticleID} to={`/help/manage/${a.ArticleID}`}
                  className={`block px-2 py-1.5 rounded text-sm hover:bg-gray-100 ${String(a.ArticleID) === String(id) ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`}>
                  <span className="inline-flex items-center gap-1.5">
                    {isGlobal
                      ? <span title="Global" className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold text-purple-700 bg-purple-50 border border-purple-200 px-1 py-0.5 rounded"><Globe className="w-3 h-3" />Global</span>
                      : <span title={a.CompanyName || 'Company'} className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1 py-0.5 rounded"><Building2 className="w-3 h-3" />Company</span>}
                    <span>{a.Title}</span>
                  </span>
                  {!a.IsPublished && <span className="ml-2 text-xs text-amber-600">draft</span>}
                  {a.Editable === false && <span className="ml-2 text-xs text-gray-400">(read-only)</span>}
                </Link>
              );
            })}
          </div>
        </aside>

        <main className="lg:col-span-3 bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">{isNew ? 'New article' : 'Edit article'}</h2>
            <div className="flex gap-2">
              <button onClick={() => setPreview(p => !p)} className="inline-flex items-center gap-1.5 border px-3 py-1.5 rounded text-sm">
                {preview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                {preview ? 'Edit' : 'Preview'}
              </button>
              {!isNew && editable && (
                <button onClick={remove} className="inline-flex items-center gap-1.5 border border-red-300 text-red-700 px-3 py-1.5 rounded text-sm hover:bg-red-50">
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              )}
              <button onClick={save} disabled={saving || !editable}
                className="inline-flex items-center gap-1.5 bg-blue-600 text-white px-4 py-1.5 rounded text-sm hover:bg-blue-700 disabled:opacity-60">
                <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>

          {!editable && !isNew && (
            <div className="mb-4 px-3 py-2 rounded border border-amber-200 bg-amber-50 text-amber-800 text-sm">
              This is a global article maintained by the platform owner. You can read it but not edit.
            </div>
          )}

          {loading ? <div className="text-gray-500">Loading…</div> : (
            <fieldset disabled={!editable} className="contents">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500">Visibility</label>
                  {isNew ? (
                    <select value={form.Scope}
                      onChange={e => setForm(f => ({ ...f, Scope: e.target.value }))}
                      className="w-full border rounded px-2 py-1.5 mt-1 text-sm">
                      <option value="company">Company only (this tenant)</option>
                      {canGlobal && <option value="global">Global (all companies)</option>}
                    </select>
                  ) : (
                    <div className="mt-1 px-2 py-1.5 text-sm rounded border bg-gray-50 inline-flex items-center gap-2">
                      {form.Scope === 'global'
                        ? <><Globe className="w-4 h-4 text-purple-600" /> Global — visible to every company</>
                        : <><Building2 className="w-4 h-4 text-emerald-600" /> Company only</>}
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-xs text-gray-500">Category</label>
                  <select value={form.CategoryID}
                    onChange={e => setForm(f => ({ ...f, CategoryID: e.target.value }))}
                    className="w-full border rounded px-2 py-1.5 mt-1 text-sm">
                    <option value="">— pick —</option>
                    {categories.map(c => <option key={c.CategoryID} value={c.CategoryID}>{c.Title}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Slug</label>
                  <input value={form.Slug}
                    onChange={e => setForm(f => ({ ...f, Slug: e.target.value.replace(/[^a-z0-9-]/gi, '-').toLowerCase() }))}
                    className="w-full border rounded px-2 py-1.5 mt-1 text-sm" placeholder="upload-an-invoice" />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Title</label>
                  <input value={form.Title}
                    onChange={e => setForm(f => ({ ...f, Title: e.target.value }))}
                    className="w-full border rounded px-2 py-1.5 mt-1 text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs text-gray-500">Summary</label>
                  <input value={form.Summary}
                    onChange={e => setForm(f => ({ ...f, Summary: e.target.value }))}
                    className="w-full border rounded px-2 py-1.5 mt-1 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Audience roles (comma list)</label>
                  <input value={form.AudienceRoles}
                    onChange={e => setForm(f => ({ ...f, AudienceRoles: e.target.value }))}
                    placeholder="Admin,Finance Admin,Employee"
                    className="w-full border rounded px-2 py-1.5 mt-1 text-sm" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500">Sort order</label>
                    <input type="number" value={form.SortOrder}
                      onChange={e => setForm(f => ({ ...f, SortOrder: Number(e.target.value) }))}
                      className="w-full border rounded px-2 py-1.5 mt-1 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Published</label>
                    <select value={form.IsPublished}
                      onChange={e => setForm(f => ({ ...f, IsPublished: Number(e.target.value) }))}
                      className="w-full border rounded px-2 py-1.5 mt-1 text-sm">
                      <option value={1}>Published</option>
                      <option value={0}>Draft</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="mt-5">
                <div className="flex items-center justify-between">
                  <label className="text-xs text-gray-500">Body (Markdown)</label>
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading || !editable}
                    className="inline-flex items-center gap-1.5 text-xs border px-2 py-1 rounded hover:bg-gray-50 disabled:opacity-60">
                    <ImageIcon className="w-3.5 h-3.5" />
                    {uploading ? 'Uploading…' : 'Upload image'}
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickImage} />
                </div>
                {preview ? (
                  <div className="border rounded-lg p-4 mt-1 min-h-[400px] bg-gray-50">
                    <Markdown source={form.BodyMarkdown} />
                  </div>
                ) : (
                  <textarea ref={bodyRef} value={form.BodyMarkdown}
                    onChange={e => setForm(f => ({ ...f, BodyMarkdown: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 mt-1 text-sm font-mono"
                    style={{ minHeight: 400 }}
                    placeholder="# Heading&#10;&#10;Paragraph text…&#10;&#10;Click 'Upload image' to add screenshots — they are stored per company (or globally for platform editors)." />
                )}
                <p className="text-xs text-gray-400 mt-1">
                  Uploaded images are stored in Azure Blob Storage. <strong>Company</strong> images live under
                  <code> helpimages/company/&lt;your-company-id&gt;/</code>; <strong>global</strong> images under
                  <code> helpimages/global/</code>. Only articles in your scope are shown to your users.
                </p>
              </div>
            </fieldset>
          )}
        </main>
      </div>
    </div>
  );
}

